import { useCallback, useState } from 'react';
import { MAX_ATTACHMENTS_PER_MESSAGE } from '@wundo/shared/attachments.ts';
import { ApiError } from '@wundo/client/api.ts';
import type { ChatAttachmentKind } from '@wundo/shared/chat.ts';
import { chatUploadRefusal, uploadChatAttachment } from './chat.ts';

/** A file picked for the next message. */
export interface PendingAttachment {
  /** How this side names it, from the moment it is picked until it is gone. */
  localId: string;
  kind: ChatAttachmentKind;
  name: string;
  size: number;
  /** What the send names it by. Absent until the upload has finished. */
  id?: string;
}

/** Why a file could not be taken, in terms the drawer can put into words. */
export type ChatAttachmentProblem =
  | 'type'
  | 'size'
  | 'too_many'
  | 'unreadable'
  | 'failed';

export interface ChatAttachments {
  pending: PendingAttachment[];
  problem: ChatAttachmentProblem | null;
  /** Whether there is still room for another file. */
  canAttach: boolean;
  add: (files: File[]) => void;
  remove: (localId: string) => void;
  clear: () => void;
}

/**
 * The files waiting to be sent with the next message.
 *
 * A file is uploaded the moment it is picked rather than when the message is
 * sent, so the wait happens while the user is still writing and the send is
 * the same instant thing it has always been. Until a message names them, the
 * uploads are nothing — closing the drawer or changing your mind leaves
 * nothing said.
 */
export function useChatAttachments(): ChatAttachments {
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [problem, setProblem] = useState<ChatAttachmentProblem | null>(null);

  const remove = useCallback((localId: string) => {
    setPending((current) => current.filter((one) => one.localId !== localId));
  }, []);

  const clear = useCallback(() => {
    setPending([]);
    setProblem(null);
  }, []);

  const add = useCallback(
    (files: File[]) => {
      const refusal = files
        .map((file) => chatUploadRefusal(file))
        .find((reason) => reason !== null);

      if (refusal) {
        setProblem(refusal);
        return;
      }

      const room = Math.max(MAX_ATTACHMENTS_PER_MESSAGE - pending.length, 0);
      setProblem(files.length > room ? 'too_many' : null);

      const taken = files.slice(0, room).map((file) => ({
        localId: crypto.randomUUID(),
        kind: file.type.startsWith('image/') ? ('image' as const) : ('file' as const),
        name: file.name,
        size: file.size,
      }));

      setPending([...pending, ...taken]);

      /*
       * Started here rather than in an effect: an upload is something the
       * picking did, not something the rendering should discover afterwards.
       * Each answer finds its own file again by id — the list will have moved
       * on by then, and a file removed mid-upload must stay removed.
       */
      taken.forEach((attachment, index) => {
        void uploadChatAttachment(files[index])
          .then((uploaded) => {
            setPending((held) =>
              held.map((one) =>
                one.localId === attachment.localId
                  ? { ...one, id: uploaded.id, kind: uploaded.kind, name: uploaded.name }
                  : one,
              ),
            );
          })
          .catch((caught: unknown) => {
            setProblem(
              caught instanceof ApiError && caught.code === 'ATTACHMENT_UNREADABLE'
                ? 'unreadable'
                : 'failed',
            );
            setPending((held) =>
              held.filter((one) => one.localId !== attachment.localId),
            );
          });
      });
    },
    [pending],
  );

  return {
    pending,
    problem,
    canAttach: pending.length < MAX_ATTACHMENTS_PER_MESSAGE,
    add,
    remove,
    clear,
  };
}
